import { useEffect, useState } from "react";
import { RefreshCw, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { acpOverview, acpSync, acpSyncLog, acpPush, acpPushLog } from "@/lib/api";
import { CloudUpload } from "lucide-react";
import { StatCard, StatusPill, Section, fmtDate } from "./acpUi";

export function AcpOverview({ caller }) {
  const [d, setD] = useState(null);
  useEffect(() => { acpOverview(caller).then(setD).catch(() => setD({ error: true })); }, [caller]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (d.error) return <div className="text-sm text-zinc-500">Couldn't load the overview.</div>;
  const rows = [
    ["Product Discovery", d.connections.product_discovery, "Product feed OpenAI reads for ChatGPT shopping results"],
    ["Instant Checkout", d.connections.instant_checkout, "Agentic Checkout API — buy inside ChatGPT"],
    ["Stripe", d.connections.stripe, "Payment capture via Stripe shared payment tokens"],
    ["ShipStation", d.connections.shipstation, "Fulfillment & tracking (existing workflow)"],
  ];
  return (
    <div className="space-y-4" data-testid="acp-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="ACP Eligible Products" value={d.cards.eligible} testId="acp-card-eligible" />
        <StatCard label="Published Products" value={d.cards.published} tone="green" testId="acp-card-published" />
        <StatCard label="Products With Errors" value={d.cards.errors} tone={d.cards.errors ? "red" : "zinc"} testId="acp-card-errors" />
        <StatCard label="Active Promotions" value={d.cards.promotions} tone="purple" testId="acp-card-promotions" />
      </div>
      <Section title="Connections">
        <ul className="divide-y divide-zinc-100">
          {rows.map(([label, status, hint]) => (
            <li key={label} className="flex items-center justify-between py-2.5" data-testid={`acp-conn-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div>
                <div className="text-sm font-medium text-zinc-900">{label}</div>
                <div className="text-xs text-zinc-500">{hint}</div>
              </div>
              <StatusPill status={status} />
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Feed snapshot">
        <div className="text-sm text-zinc-700">{d.feed.items} items · last generated {fmtDate(d.feed.generated_at)}</div>
        <div className="text-xs text-zinc-500 mt-1">ChatGPT orders so far: <b>{d.cards.orders}</b>. Use the Sync tab to regenerate the feed after catalogue changes.</div>
      </Section>
    </div>
  );
}

export function AcpFeed({ caller }) {
  const [d, setD] = useState(null);
  useEffect(() => { acpOverview(caller).then(setD).catch(() => {}); }, [caller]);
  const url = d?.feed?.public_url;
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Feed URL copied"));
  const pushed = !!d?.push?.ok;
  const steps = [
    ["Apply as a merchant", "chatgpt.com/merchants — Product Discovery (checkout optional)", "done"],
    ["Wait for OpenAI approval", "You'll get an email with SFTP host, username and key", d?.push?.configured || pushed ? "done" : "pending"],
    ["Add the SFTP details", "Settings tab → OpenAI SFTP delivery → Save → Test connection", d?.push?.configured ? "done" : "todo"],
    ["Push the feed", "Sync tab → Push to OpenAI now (then nightly automatically)", pushed ? "done" : "todo"],
    ["Meanwhile: organic discovery", "robots.txt allows OAI-SearchBot, product sitemap + Product schema are live", "done"],
  ];
  return (
    <div className="space-y-4" data-testid="acp-feed">
      <Section title="Product feed (OpenAI file-upload spec, JSONL)">
        <p className="text-xs text-zinc-500 mb-3">One line per product: item_id, title, description, url, seller_name, image_url, availability, price, brand, eligibility flags. ChatGPT does not crawl this URL — OpenAI only ingests snapshots pushed to the SFTP destination they issue after approval. This URL is for review and manual upload.</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate text-xs bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2" data-testid="acp-feed-url">{url || "Run a sync to generate the feed"}</code>
          <button type="button" onClick={copy} disabled={!url} className="h-9 px-3 rounded-lg border border-zinc-200 text-xs font-semibold inline-flex items-center gap-1 hover:bg-zinc-50 disabled:opacity-50" data-testid="acp-feed-copy"><Copy size={13} /> Copy</button>
          <a href={url || "#"} target="_blank" rel="noreferrer" className={`h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold inline-flex items-center ${!url ? "pointer-events-none opacity-50" : ""}`} data-testid="acp-feed-open">Open</a>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500 mt-3">
          <span>{d ? `${d.feed.items} items · generated ${fmtDate(d.feed.generated_at)}` : ""}</span>
          {d && <a href={d.feed.gz_url} className="underline" data-testid="acp-feed-gz">Download .jsonl.gz (upload format)</a>}
          {d && <a href={d.feed.sitemap_url} target="_blank" rel="noreferrer" className="underline" data-testid="acp-feed-sitemap">Product sitemap</a>}
        </div>
      </Section>
      <Section title="How your products get into ChatGPT">
        <ol className="space-y-2" data-testid="acp-feed-steps">
          {steps.map(([t, h, s], i) => (
            <li key={t} className="flex items-start gap-3">
              <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${s === "done" ? "bg-emerald-600 text-white" : s === "pending" ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-700"}`}>{s === "done" ? "✓" : i + 1}</span>
              <div><div className="text-sm font-medium text-zinc-900">{t}</div><div className="text-xs text-zinc-500">{h}</div></div>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

export function AcpSync({ caller }) {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [push, setPush] = useState(null);
  const [pushing, setPushing] = useState(false);
  const load = () => { acpSyncLog(caller).then((r) => setRuns(r.runs || [])).catch(() => {}); acpPushLog(caller).then(setPush).catch(() => {}); };
  async function pushNow() {
    setPushing(true);
    try {
      const r = await acpPush(caller);
      toast.success(r.method === "sftp" ? `Uploaded ${r.products} products to ${r.remote}` : `Pushed ${r.products} products to OpenAI feed ${r.feed_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  }
  useEffect(() => { load(); }, [caller]); // eslint-disable-line react-hooks/exhaustive-deps
  async function run() {
    setBusy(true);
    try {
      const r = await acpSync(caller);
      toast.success(`Feed synced — ${r.count} items${r.errors ? `, ${r.errors} with errors` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4" data-testid="acp-sync">
      <Section title="Sync" right={
        <button type="button" onClick={run} disabled={busy} className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="acp-sync-now">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync now
        </button>}>
        <p className="text-xs text-zinc-500">Rebuilds the ACP feed from the live catalogue (prices, inventory, images), validates every published product and records the result. Run it after catalogue edits.</p>
      </Section>
      <Section title={push?.method === "sftp" ? "Push to OpenAI (merchant SFTP)" : "Push to OpenAI"} right={
        <button type="button" onClick={pushNow} disabled={pushing || !push?.configured} className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="acp-push-now">
          {pushing ? <Loader2 size={13} className="animate-spin" /> : <CloudUpload size={13} />} Push to OpenAI now
        </button>}>
        {push?.configured ? (
          <p className="text-xs text-zinc-500">{push.method === "sftp"
            ? "Uploads the full catalogue snapshot (products.jsonl.gz) to your OpenAI merchant SFTP folder, overwriting the previous file."
            : <>Upserts every published product into your OpenAI product feed{push.feed_id ? <> (<span className="font-mono">{push.feed_id}</span>)</> : ""}.</>} Also runs automatically every night at 07:15 UTC.</p>
        ) : (
          <div className="text-xs rounded-lg bg-amber-50 border border-amber-100 text-amber-800 p-3" data-testid="acp-push-notice">
            <b>Waiting for your merchant approval.</b> ChatGPT only ingests feeds pushed to the SFTP destination OpenAI issues after approving your application at <a className="underline" href="https://chatgpt.com/merchants" target="_blank" rel="noreferrer">chatgpt.com/merchants</a>. When the approval email arrives, paste the SFTP host, username and key under <b>Settings → OpenAI SFTP delivery</b> and this button goes live.
          </div>
        )}
        {push?.runs?.length > 0 && (
          <ul className="divide-y divide-zinc-100 text-sm mt-3" data-testid="acp-push-runs">
            {push.runs.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-2">
                <span className="text-zinc-700">{fmtDate(r.at)} · {r.trigger}{r.method ? ` · ${r.method}` : ""}</span>
                <span className={`text-xs ${r.ok ? "text-emerald-600" : "text-rose-600"}`} title={r.detail || ""}>{r.ok ? `${r.products} products ${r.method === "sftp" ? "uploaded" : "accepted"}` : `failed (${r.status})`}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Recent feed builds">
        {runs.length === 0 ? <div className="text-xs text-zinc-500">No syncs yet.</div> : (
          <ul className="divide-y divide-zinc-100 text-sm" data-testid="acp-sync-runs">
            {runs.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-2">
                <span className="text-zinc-700">{fmtDate(r.generated_at)}</span>
                <span className="text-xs text-zinc-500">{r.count} items · {r.errors} errors · {(r.bytes / 1024).toFixed(1)} KB</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
